import {
  Button as MuiButton,
  CircularProgress,
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Checkbox,
  InputAdornment,
} from '@mui/material';
import {
  ChevronDown,
  ExternalLink,
  FileText,
  Layers,
  Plus,
  RefreshCw,
  Save,
  Search,
  Trash2,
} from 'lucide-react';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { globalSnackbar } from 'components/GlobalSnackbar';
import { YandexTrackerApi } from 'api/yandex-tracker';
import {
  Button as ShellButton,
  IconButton as ShellIconButton,
  EmptyState,
  SelectionBar,
} from 'components/AppShell';
import type { YandexTrackerGroupItem } from 'model/yandex-tracker';

const TRACKER_TASK_URL = 'https://tracker.yandex.ru';
const SEARCH_DEBOUNCE_MS = 300;
const PAGE_SIZE = 5;

/** ID задач/эпиков с флагом контекста с сервера (по уже загруженному дереву). */
function collectContextEnabledIds(
  roots: YandexTrackerGroupItem[],
  epicChildren: Record<string, YandexTrackerGroupItem[]>
): Set<string> {
  const out = new Set<string>();
  const walk = (node: YandexTrackerGroupItem) => {
    if (node.is_context_enabled) out.add(node.id);
    const children = epicChildren[node.id];
    if (children?.length) children.forEach(walk);
  };
  roots.forEach(walk);
  return out;
}

/** ID элемента + все загруженные потомки (рекурсивно). */
function getAllDescendantIds(
  item: YandexTrackerGroupItem,
  epicChildren: Record<string, YandexTrackerGroupItem[]>
): Set<string> {
  const ids = new Set<string>([item.id]);
  const children = epicChildren[item.id];
  if (children) {
    for (const child of children) {
      Array.from(getAllDescendantIds(child, epicChildren)).forEach((id) => ids.add(id));
    }
  }
  return ids;
}

function findItemById(
  id: string,
  items: YandexTrackerGroupItem[],
  epicChildren: Record<string, YandexTrackerGroupItem[]>
): YandexTrackerGroupItem | null {
  const inItems = items.find((i) => i.id === id);
  if (inItems) return inItems;
  for (const arr of Object.values(epicChildren)) {
    const found = arr.find((i) => i.id === id);
    if (found) return found;
  }
  return null;
}

function ContextChip(): React.ReactElement {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '1px 7px',
        fontSize: 10,
        fontWeight: 600,
        borderRadius: 999,
        background: 'var(--shell-accent-subtle)',
        color: 'var(--shell-accent-pressed)',
        border: '1px solid var(--shell-accent-primary)',
        lineHeight: 1.4,
      }}
    >
      В контексте
    </span>
  );
}

function TaskCard({
  item,
  onDeleteClick,
  deletingId,
  selected,
  onToggleSelect,
  depth = 0,
}: {
  item: YandexTrackerGroupItem;
  onDeleteClick?: (id: string, type: string) => void;
  deletingId?: string | null;
  selected?: boolean;
  onToggleSelect?: (item: YandexTrackerGroupItem) => void;
  depth?: number;
}): React.ReactElement {
  const url = `${TRACKER_TASK_URL}/${item.key}`;
  const isDeleting = deletingId === item.id;
  const indent = Math.min(depth, 5) * 18;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 8,
        padding: '8px 12px',
        paddingLeft: 12 + indent,
        borderBottom: '1px solid var(--shell-border-subtle)',
        background: selected ? 'var(--shell-accent-subtle)' : 'transparent',
        transition: 'background var(--shell-transition-fast)',
      }}
      onMouseEnter={(e) => {
        if (!selected) e.currentTarget.style.background = 'var(--shell-bg-subtle)';
      }}
      onMouseLeave={(e) => {
        if (!selected) e.currentTarget.style.background = 'transparent';
      }}
    >
      {onToggleSelect && (
        <Checkbox
          checked={selected ?? false}
          onChange={() => onToggleSelect(item)}
          onClick={(e) => e.stopPropagation()}
          size="small"
          sx={{ p: 0.5, mt: 0 }}
        />
      )}
      <div
        style={{
          width: 26,
          height: 26,
          borderRadius: 6,
          background: selected ? 'var(--shell-accent-primary)' : 'var(--shell-bg-subtle)',
          color: selected ? 'var(--shell-accent-on-accent)' : 'var(--shell-text-tertiary)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          marginTop: 1,
        }}
      >
        <FileText size={14} strokeWidth={1.75} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 500,
            color: 'var(--shell-text-primary)',
            lineHeight: 1.35,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {item.summary}
        </div>
        <div
          style={{
            display: 'flex',
            gap: 8,
            alignItems: 'center',
            marginTop: 2,
            fontSize: 11,
            color: 'var(--shell-text-tertiary)',
          }}
        >
          <span style={{ fontFamily: 'var(--shell-font-mono, monospace)' }}>{item.key}</span>
          {item.is_context_enabled && <ContextChip />}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 0, flexShrink: 0 }}>
        <ShellIconButton
          size="sm"
          label="Открыть"
          onClick={() => window.open(url, '_blank')}
          icon={<ExternalLink size={14} strokeWidth={1.75} />}
        />
        {onDeleteClick && (
          <ShellIconButton
            size="sm"
            tone="danger"
            label="Удалить"
            disabled={isDeleting}
            onClick={() => onDeleteClick(item.id, item.type)}
            icon={
              isDeleting ? (
                <CircularProgress size={14} sx={{ color: 'var(--shell-semantic-error)' }} />
              ) : (
                <Trash2 size={14} strokeWidth={1.75} />
              )
            }
          />
        )}
      </div>
    </div>
  );
}

type TaskTreeNodeContext = {
  epicChildren: Record<string, YandexTrackerGroupItem[]>;
  epicCounts: Record<string, number>;
  loadingEpicIds: Set<string>;
  epicLoadingMore: Record<string, boolean>;
  onAccordionChange: (id: string) => (e: React.SyntheticEvent, expanded: boolean) => void;
  loadMoreEpic: (id: string) => void;
  onDeleteClick: (id: string, type: string) => void;
  deletingId: string | null;
  selectedIds: Set<string>;
  onToggleSelect: (item: YandexTrackerGroupItem) => void;
};

function TaskTreeNode({
  item,
  ctx,
  depth = 0,
}: {
  item: YandexTrackerGroupItem;
  ctx: TaskTreeNodeContext;
  depth?: number;
}): React.ReactElement {
  if (!item.has_children) {
    return (
      <TaskCard
        item={item}
        onDeleteClick={ctx.onDeleteClick}
        deletingId={ctx.deletingId}
        selected={ctx.selectedIds.has(item.id)}
        onToggleSelect={ctx.onToggleSelect}
        depth={depth}
      />
    );
  }

  const children = ctx.epicChildren[item.id];
  const childrenCount = ctx.epicCounts[item.id] ?? 0;
  const isLoading = ctx.loadingEpicIds.has(item.id);
  const isLoadingMore = Boolean(ctx.epicLoadingMore[item.id]);

  const epicSelected = ctx.selectedIds.has(item.id);

  const isNestedEpic = depth > 0;

  const indent = Math.min(depth, 5) * 18;

  return (
    <Accordion
      key={item.id}
      disableGutters
      elevation={0}
      onChange={ctx.onAccordionChange(item.id)}
      sx={{
        borderBottom: '1px solid var(--shell-border-subtle)',
        background: epicSelected ? 'var(--shell-accent-subtle)' : 'transparent',
        '&:before': { display: 'none' },
        boxShadow: 'none',
      }}
    >
      <AccordionSummary
        expandIcon={<ChevronDown size={14} strokeWidth={1.75} color="var(--shell-text-tertiary)" />}
        sx={{
          minHeight: 'auto !important',
          padding: '0 12px',
          paddingLeft: `${12 + indent}px`,
          '&:hover': { background: 'var(--shell-bg-subtle)' },
          '& .MuiAccordionSummary-content': {
            alignItems: 'center',
            gap: 1,
            my: '8px !important',
          },
          '& .MuiAccordionSummary-expandIconWrapper': {
            transition: 'transform 0.2s',
          },
        }}
      >
        <Checkbox
          checked={epicSelected}
          onChange={() => ctx.onToggleSelect(item)}
          onClick={(e) => e.stopPropagation()}
          size="small"
          sx={{ p: 0.5 }}
        />
        <div
          style={{
            width: 26,
            height: 26,
            borderRadius: 6,
            background: 'var(--shell-accent-primary)',
            color: 'var(--shell-accent-on-accent)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Layers size={14} strokeWidth={1.75} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: 'var(--shell-text-primary)',
              lineHeight: 1.35,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {item.summary}
          </div>
          <div
            style={{
              display: 'flex',
              gap: 8,
              alignItems: 'center',
              marginTop: 2,
              fontSize: 11,
              color: 'var(--shell-text-tertiary)',
            }}
          >
            <span style={{ fontFamily: 'var(--shell-font-mono, monospace)' }}>{item.key}</span>
            <span>·</span>
            <span>{isNestedEpic ? 'вложенный эпик' : 'эпик'}</span>
            {childrenCount > 0 && (
              <>
                <span>·</span>
                <span>{childrenCount} внутри</span>
              </>
            )}
            {item.is_context_enabled && <ContextChip />}
          </div>
        </div>
        <span onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex' }}>
          <ShellIconButton
            size="sm"
            tone="danger"
            label="Удалить"
            disabled={ctx.deletingId === item.id}
            onClick={() => ctx.onDeleteClick(item.id, item.type)}
            icon={
              ctx.deletingId === item.id ? (
                <CircularProgress size={14} sx={{ color: 'var(--shell-semantic-error)' }} />
              ) : (
                <Trash2 size={14} strokeWidth={1.75} />
              )
            }
          />
        </span>
      </AccordionSummary>
      <AccordionDetails
        sx={{
          px: 0,
          pt: 0,
          pb: 0,
          display: 'flex',
          flexDirection: 'column',
          background: 'var(--shell-bg-canvas)',
          borderTop: '1px solid var(--shell-border-subtle)',
        }}
      >
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={18} sx={{ color: 'var(--shell-accent-primary)' }} />
          </Box>
        )}
        {children?.length === 0 && !isLoading && (
          <Box sx={{ px: 1.5, py: 1.5 }}>
            <Typography
              variant="caption"
              sx={{ color: 'var(--shell-text-tertiary)', fontSize: 12 }}
            >
              Нет дочерних элементов
            </Typography>
          </Box>
        )}
        {children?.map((child) => (
          <TaskTreeNode key={child.id} item={child} ctx={ctx} depth={depth + 1} />
        ))}
        {children && children.length < childrenCount && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 1, px: 1.5 }}>
            <ShellButton
              variant="secondary"
              size="sm"
              onClick={() => ctx.loadMoreEpic(item.id)}
              disabled={isLoadingMore}
              leftIcon={isLoadingMore ? <CircularProgress size={12} /> : undefined}
            >
              {isLoadingMore ? 'Загрузка…' : 'Показать ещё'}
            </ShellButton>
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
}

function TasksTab(): React.ReactElement {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<YandexTrackerGroupItem[]>([]);
  const [epicChildren, setEpicChildren] = useState<Record<string, YandexTrackerGroupItem[]>>({});
  const [epicCounts, setEpicCounts] = useState<Record<string, number>>({});
  const [epicLoadingMore, setEpicLoadingMore] = useState<Record<string, boolean>>({});
  const [loadingEpicIds, setLoadingEpicIds] = useState<Set<string>>(new Set());
  const [taskUrl, setTaskUrl] = useState('');
  const [isAddingTask, setIsAddingTask] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [itemToDelete, setItemToDelete] = useState<{ id: string; type: string } | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [contextSelectionDirty, setContextSelectionDirty] = useState(false);
  const [contextSaving, setContextSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState<{ total?: number; done?: number } | null>(null);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const syncPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const currentProject = useSelector(
    (state: { reducer: { project?: { id?: string } } }) => state.reducer.project
  );

  useEffect(() => {
    setContextSelectionDirty(false);
    setSelectedIds(new Set());
  }, [currentProject?.id]);

  useEffect(() => {
    searchDebounceRef.current = setTimeout(() => {
      setDebouncedQuery(searchInput);
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [searchInput]);

  const fetchGroups = useCallback(
    async (query?: string) => {
      if (!currentProject?.id) return;

      const searchQuery = (query ?? debouncedQuery).trim() || undefined;
      setLoading(true);
      try {
        const res = await YandexTrackerApi.getGroups({
          params: {
            project_id: currentProject.id,
            query: searchQuery,
          },
        });
        setItems(res.items ?? []);
        setEpicChildren({});
        setEpicCounts({});
        setEpicLoadingMore({});
      } catch (error) {
        const err = error as Error;
        console.error('Ошибка загрузки групп задач:', err);
        globalSnackbar({
          message: `Ошибка загрузки: ${err.message}`,
          severity: 'error',
        });
      } finally {
        setLoading(false);
      }
    },
    [currentProject?.id, debouncedQuery]
  );

  useEffect(() => {
    if (currentProject?.id) {
      fetchGroups(debouncedQuery);
    }
  }, [currentProject?.id, debouncedQuery, fetchGroups]);

  /** Синхронизация выбора с флагом is_context_enabled с сервера (как вики). */
  useEffect(() => {
    if (!currentProject?.id) return;

    if (contextSelectionDirty) {
      setSelectedIds((prev) => {
        const valid = new Set<string>();
        const collectIds = (list: YandexTrackerGroupItem[]) => {
          for (const it of list) {
            valid.add(it.id);
            const ch = epicChildren[it.id];
            if (ch?.length) collectIds(ch);
          }
        };
        collectIds(items);
        let changed = false;
        const next = new Set<string>();
        prev.forEach((id) => {
          if (valid.has(id)) next.add(id);
          else changed = true;
        });
        if (!changed && next.size === prev.size) return prev;
        return next;
      });
      return;
    }

    setSelectedIds(collectContextEnabledIds(items, epicChildren));
  }, [currentProject?.id, items, epicChildren, contextSelectionDirty]);

  // При загрузке дочерних элементов выбранной группы — добавляем их в выбор
  useEffect(() => {
    setSelectedIds((prev) => {
      let next: Set<string> | null = null;
      Array.from(prev).forEach((id) => {
        const children = epicChildren[id];
        if (!children?.length) return;
        const item = findItemById(id, items, epicChildren);
        if (!item) return;
        const descendants = getAllDescendantIds(item, epicChildren);
        const missing = Array.from(descendants).filter((d) => !prev.has(d));
        if (missing.length > 0) {
          if (next == null) next = new Set(prev);
          missing.forEach((d) => next?.add(d));
        }
      });
      return next ?? prev;
    });
  }, [epicChildren, items]);

  const fetchEpicTasks = useCallback(
    async (epicId: string) => {
      if (!currentProject?.id) return;

      setLoadingEpicIds((prev) => new Set(prev).add(epicId));
      try {
        const res = await YandexTrackerApi.getGroupTasks({
          params: {
            id: epicId,
            project_id: currentProject.id,
            limit: PAGE_SIZE,
            offset: 0,
          },
        });
        setEpicChildren((prev) => ({ ...prev, [epicId]: res.items ?? [] }));
        setEpicCounts((prev) => ({ ...prev, [epicId]: res.count ?? 0 }));
      } catch (error) {
        const err = error as Error;
        globalSnackbar({
          message: `Ошибка загрузки задач эпика: ${err.message}`,
          severity: 'error',
        });
      } finally {
        setLoadingEpicIds((prev) => {
          const next = new Set(prev);
          next.delete(epicId);
          return next;
        });
      }
    },
    [currentProject?.id]
  );

  const handleAccordionChange =
    (epicId: string) => (_: React.SyntheticEvent, expanded: boolean) => {
      if (expanded && epicChildren[epicId] === undefined && !loadingEpicIds.has(epicId)) {
        fetchEpicTasks(epicId);
      }
    };

  const loadMoreEpic = useCallback(
    async (epicId: string) => {
      if (!currentProject?.id || epicLoadingMore[epicId]) return;

      const offset = epicChildren[epicId]?.length ?? 0;
      setEpicLoadingMore((prev) => ({ ...prev, [epicId]: true }));
      try {
        const res = await YandexTrackerApi.getGroupTasks({
          params: {
            id: epicId,
            project_id: currentProject.id,
            limit: PAGE_SIZE,
            offset,
          },
        });
        setEpicChildren((prev) => ({
          ...prev,
          [epicId]: [...(prev[epicId] ?? []), ...(res.items ?? [])],
        }));
        setEpicCounts((prev) => ({ ...prev, [epicId]: res.count ?? 0 }));
      } catch (error) {
        const err = error as Error;
        globalSnackbar({ message: `Ошибка загрузки: ${err.message}`, severity: 'error' });
      } finally {
        setEpicLoadingMore((prev) => ({ ...prev, [epicId]: false }));
      }
    },
    [currentProject?.id, epicChildren, epicLoadingMore]
  );

  const handleDelete = useCallback(
    async (id: string) => {
      setDeletingId(id);
      try {
        await YandexTrackerApi.deleteTask({ params: { id } });
        globalSnackbar({ message: 'Удалено', severity: 'success' });
        await fetchGroups(debouncedQuery);
      } catch (error) {
        const err = error as Error;
        globalSnackbar({ message: `Ошибка удаления: ${err.message}`, severity: 'error' });
      } finally {
        setDeletingId(null);
      }
    },
    [debouncedQuery, fetchGroups]
  );

  const handleAddTask = async () => {
    if (!taskUrl.trim()) {
      globalSnackbar({ message: 'Введите URL задачи', severity: 'warning' });
      return;
    }
    if (!currentProject?.id) {
      globalSnackbar({ message: 'Не выбран проект', severity: 'warning' });
      return;
    }
    setIsAddingTask(true);
    try {
      await YandexTrackerApi.addTask({
        body: { url: taskUrl.trim(), project_id: currentProject.id },
      });
      globalSnackbar({ message: 'Задача успешно добавлена', severity: 'success' });
      setTaskUrl('');
      await fetchGroups(debouncedQuery);
    } catch (error) {
      const err = error as Error;
      globalSnackbar({ message: `Ошибка добавления задачи: ${err.message}`, severity: 'error' });
    } finally {
      setIsAddingTask(false);
    }
  };

  const onToggleSelect = useCallback(
    (item: YandexTrackerGroupItem) => {
      setContextSelectionDirty(true);
      const ids = item.has_children ? getAllDescendantIds(item, epicChildren) : new Set([item.id]);
      const idList = Array.from(ids);
      setSelectedIds((prev) => {
        const next = new Set(prev);
        const anySelected = idList.some((id) => next.has(id));
        if (anySelected) idList.forEach((id) => next.delete(id));
        else idList.forEach((id) => next.add(id));
        return next;
      });
    },
    [epicChildren]
  );

  const handleSync = useCallback(async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) {
      globalSnackbar({ message: 'Выберите задачи для синхронизации', severity: 'warning' });
      return;
    }
    setSyncing(true);
    setSyncStatus(null);
    try {
      const res = await YandexTrackerApi.sync({ body: { task_ids: ids } });
      globalSnackbar({
        message: `Синхронизация запущена (${res.task_id})`,
        severity: 'success',
      });
      const pollStatus = async () => {
        try {
          const status = await YandexTrackerApi.getSyncStatus({
            params: { task_id: res.task_id },
          });
          setSyncStatus({ total: status.total, done: status.done });
          if (status.status === 'done' || status.status === 'failed') {
            if (syncPollRef.current) {
              clearInterval(syncPollRef.current);
              syncPollRef.current = null;
            }
            setSyncing(false);
            if (status.status === 'done') {
              globalSnackbar({
                message:
                  status.done != null && status.total != null
                    ? `Синхронизация завершена: ${status.done}/${status.total}`
                    : 'Синхронизация завершена',
                severity: 'success',
              });
              setSelectedIds(new Set());
              setContextSelectionDirty(false);
              await fetchGroups(debouncedQuery);
            } else {
              globalSnackbar({
                message: 'Синхронизация завершилась с ошибкой',
                severity: 'error',
              });
            }
          }
        } catch {
          // игнорируем ошибки опроса
        }
      };
      await pollStatus();
      syncPollRef.current = setInterval(pollStatus, 2000);
    } catch (error) {
      const err = error as Error;
      globalSnackbar({ message: `Ошибка синхронизации: ${err.message}`, severity: 'error' });
      setSyncing(false);
    }
  }, [selectedIds, debouncedQuery, fetchGroups]);

  const handleSaveToContext = useCallback(async () => {
    if (!currentProject?.id || selectedIds.size === 0) return;
    setContextSaving(true);
    try {
      await YandexTrackerApi.saveTasks({
        body: {
          project_id: currentProject.id,
          task_ids: Array.from(selectedIds),
        },
      });
      globalSnackbar({
        message: `Сохранено в контекст: ${selectedIds.size}`,
        severity: 'success',
      });
      await fetchGroups(debouncedQuery);
      setContextSelectionDirty(false);
    } catch (error) {
      const err = error as Error;
      globalSnackbar({
        message: `Ошибка сохранения в контекст: ${err.message}`,
        severity: 'error',
      });
    } finally {
      setContextSaving(false);
    }
  }, [currentProject?.id, selectedIds, debouncedQuery, fetchGroups]);

  const handleClearContext = useCallback(async () => {
    if (!currentProject?.id) return;
    setContextSaving(true);
    try {
      await YandexTrackerApi.saveTasks({
        body: { project_id: currentProject.id, task_ids: [] },
      });
      globalSnackbar({
        message: 'Контекст отключён для всех задач трекера в проекте',
        severity: 'info',
      });
      setSelectedIds(new Set());
      await fetchGroups(debouncedQuery);
      setContextSelectionDirty(false);
    } catch (error) {
      const err = error as Error;
      globalSnackbar({
        message: `Ошибка: ${err.message}`,
        severity: 'error',
      });
    } finally {
      setContextSaving(false);
    }
  }, [currentProject?.id, debouncedQuery, fetchGroups]);

  useEffect(() => {
    return () => {
      if (syncPollRef.current) clearInterval(syncPollRef.current);
    };
  }, []);

  const taskTreeContext: TaskTreeNodeContext = {
    epicChildren,
    epicCounts,
    loadingEpicIds,
    epicLoadingMore,
    onAccordionChange: handleAccordionChange,
    loadMoreEpic,
    onDeleteClick: (id, type) => setItemToDelete({ id, type }),
    deletingId,
    selectedIds,
    onToggleSelect,
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
        gap: 1.5,
      }}
    >
      {!currentProject?.id && (
        <EmptyState
          size="sm"
          title="Выберите проект"
          description="Задачи привязаны к проекту. Выберите проект в шапке."
        />
      )}

      {currentProject?.id && (
        <>
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              flexWrap: 'wrap',
              alignItems: 'center',
              flexShrink: 0,
            }}
          >
            <TextField
              placeholder="https://tracker.yandex.ru/CONTEXT-1"
              size="small"
              sx={{
                flex: '1 1 280px',
                minWidth: 220,
                '& .MuiOutlinedInput-root': { height: 32, fontSize: 13 },
              }}
              value={taskUrl}
              onChange={(e) => setTaskUrl(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') handleAddTask();
              }}
            />
            <ShellButton
              variant="secondary"
              leftIcon={<Plus size={14} strokeWidth={2} />}
              onClick={handleAddTask}
              disabled={isAddingTask || !taskUrl.trim()}
            >
              {isAddingTask ? 'Добавление…' : 'Добавить задачу'}
            </ShellButton>
            <ShellIconButton
              label="Обновить список"
              onClick={() => fetchGroups(debouncedQuery)}
              disabled={loading}
              icon={
                loading ? (
                  <CircularProgress size={14} sx={{ color: 'var(--shell-accent-primary)' }} />
                ) : (
                  <RefreshCw size={14} strokeWidth={1.75} />
                )
              }
            />
          </Box>

          <TextField
            placeholder="Поиск по задачам и эпикам…"
            size="small"
            fullWidth
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            sx={{
              flexShrink: 0,
              '& .MuiOutlinedInput-root': { height: 32, fontSize: 13 },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={14} strokeWidth={1.75} color="var(--shell-text-tertiary)" />
                </InputAdornment>
              ),
            }}
          />

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: 'auto',
              border: '1px solid var(--shell-border-subtle)',
              borderRadius: 'var(--shell-radius-lg)',
              background: 'var(--shell-bg-surface)',
            }}
          >
            {loading && items.length === 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={20} sx={{ color: 'var(--shell-accent-primary)' }} />
              </Box>
            )}

            {!loading && items.length === 0 && (
              <EmptyState
                title="Задач и эпиков пока нет"
                description="Добавьте задачу по URL или обновите список."
              />
            )}

            {items.length > 0 && (
              <>
                <Box
                  sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    px: 1.5,
                    py: 1,
                    borderBottom: '1px solid var(--shell-border-subtle)',
                    background: 'var(--shell-bg-subtle)',
                    fontSize: 11,
                    color: 'var(--shell-text-tertiary)',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: 0.6,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <span>
                    {items.length} в списке · выбрано: {selectedIds.size}
                  </span>
                  {syncing && syncStatus?.total != null && syncStatus.done != null && (
                    <span style={{ color: 'var(--shell-accent-pressed)', fontWeight: 600 }}>
                      Sync {syncStatus.done}/{syncStatus.total}
                    </span>
                  )}
                </Box>
                {items.map((item) => (
                  <TaskTreeNode key={item.id} item={item} ctx={taskTreeContext} depth={0} />
                ))}
              </>
            )}
          </Box>

          <SelectionBar
            count={selectedIds.size}
            hint={syncing ? 'Идёт синхронизация…' : `из ${items.length}`}
          >
            <ShellButton
              variant="ghost"
              onClick={handleClearContext}
              disabled={contextSaving || loading}
            >
              Снять все из контекста
            </ShellButton>
            <ShellButton
              variant="secondary"
              leftIcon={
                syncing ? (
                  <CircularProgress size={12} sx={{ color: 'currentColor' }} />
                ) : (
                  <RefreshCw size={14} strokeWidth={1.75} />
                )
              }
              onClick={handleSync}
              disabled={syncing}
            >
              {syncing ? 'Синхронизация…' : 'Синхронизировать'}
            </ShellButton>
            <ShellButton
              variant="primary"
              leftIcon={<Save size={14} strokeWidth={1.75} />}
              onClick={handleSaveToContext}
              disabled={contextSaving || loading}
            >
              {contextSaving ? 'Сохранение…' : 'Сохранить в контекст'}
            </ShellButton>
          </SelectionBar>
        </>
      )}

      <Dialog
        open={itemToDelete !== null}
        onClose={() => setItemToDelete(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: 16, fontWeight: 600 }}>Удалить?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: 13 }}>
            {itemToDelete?.type === 'epic'
              ? 'Эпик и его связь с проектом будут удалены. Действие нельзя отменить.'
              : 'Задача будет удалена из проекта. Действие нельзя отменить.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MuiButton onClick={() => setItemToDelete(null)}>Отмена</MuiButton>
          <MuiButton
            color="error"
            variant="contained"
            onClick={async () => {
              if (!itemToDelete) return;
              await handleDelete(itemToDelete.id);
              setItemToDelete(null);
            }}
            disabled={deletingId === itemToDelete?.id}
          >
            {deletingId === itemToDelete?.id ? 'Удаление…' : 'Удалить'}
          </MuiButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default TasksTab;
